import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'
import {
    bundleIcon,
    CalendarAddFilled,
    CalendarAddRegular,
    CallAddFilled,
    CallAddRegular,
    DocumentAddFilled,
    DocumentAddRegular,
    MailAddFilled,
    MailAddRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { FilePartyDto } from '../../../../config/filePartyConfig'
import { IFileDto } from '../../../../config/fileConfig'
import { composeAppointmentClick, composeEmailClick } from '../../../../helpers/partyComposeHelper'
import { isAppContext } from '../../../../helpers/appHelper'
import { AppContext } from '../../../../config/appConfig'
import { isOfficeInitialized } from '../../../../helpers/outlookHelper'
import { formatSubject } from '../../../../components/Dialog/OutlookItem/ComposeOutlookItemDialog'
import { DocumentContext } from '../../../../config/documentConfig'
import HammockFormDialog from '../../../components/Dialog/HammockFormDialog'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/OfficeDocumentDialogBody'
import { useFileFolderTree } from '../../../../context/FileFolderTreeContext'
import OverflowToolbarPanel, { HammockToolbarButtonData } from '../../../../components/Overflow/OverflowToolbarPanel'

const useStyles = makeStyles({
    // Overflow measures this container's width; give it the full row width so buttons only collapse into the
    // overflow menu when space really runs out (narrow sidepanel), not while there is room.
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
        width: '100%',
        minWidth: 0,
    },
})

const CallAddIcon = bundleIcon(CallAddFilled, CallAddRegular)
const CalendarAddIcon = bundleIcon(CalendarAddFilled, CalendarAddRegular)
const DocumentAddIcon = bundleIcon(DocumentAddFilled, DocumentAddRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)

export type FilePartyActionsProps = {
    party: FilePartyDto
    file: IFileDto
    // Extra actions merged into the toolbar. `leadingButtons` render before mail (e.g. add-contactpersoon),
    // `trailingButtons` after call (e.g. ProofMe, delete). Items with custom UI (popovers/dialogs) use `render`.
    leadingButtons?: HammockToolbarButtonData[]
    trailingButtons?: HammockToolbarButtonData[]
}

/**
 * Shared action row for a party (bedrijf or contactpersoon): mail, appointment, call and letter, rendered through
 * OverflowToolbarPanel so spacing/sizing are consistent and buttons collapse into an overflow menu in the narrow
 * (sidepanel) viewport. Mail/appointment/call are disabled with an explanatory tooltip when the contact detail is
 * missing. Party-specific extras (add-contact, ProofMe, delete) are passed in via leading/trailing buttons.
 */
export default function FilePartyActions(props: FilePartyActionsProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { folders, setFolders: persistFolders } = useFileFolderTree()
    const [newDocumentDialogOpen, setNewDocumentDialogOpen] = useState(false)

    const { party, file } = props
    const subject = formatSubject(file, [party])
    const showAppointment = isOfficeInitialized() && !isAppContext([AppContext.Compose])

    const buttons = useMemo<HammockToolbarButtonData[]>(
        () => [
            ...(props.leadingButtons ?? []),
            {
                value: 'mail',
                icon: <MailAddIcon />,
                title: party.email ? translate('file.pivot.party.card.mail', { email: party.email }) : translate('file.pivot.party.card.noemail'),
                disabled: !party.email,
                onClick: () => composeEmailClick(party, subject)(),
            },
            ...(showAppointment
                ? [
                      {
                          value: 'appointment',
                          icon: <CalendarAddIcon />,
                          title: party.email ? translate('file.pivot.party.card.appointment') : translate('file.pivot.party.card.noemail'),
                          disabled: !party.email,
                          onClick: () => composeAppointmentClick(party, subject)(),
                      },
                  ]
                : []),
            {
                value: 'document',
                icon: <DocumentAddIcon />,
                title: translate('file.pivot.party.card.document'),
                onClick: () => setNewDocumentDialogOpen(true),
            },
            {
                value: 'call',
                icon: <CallAddIcon />,
                title: party.phoneNumber
                    ? translate('file.pivot.party.card.call', { telephone: party.phoneNumber })
                    : translate('file.pivot.party.card.nophone'),
                disabled: !party.phoneNumber,
                onClick: () => window.open(`tel:${party.phoneNumber}`),
            },
            ...(props.trailingButtons ?? []),
        ],
        [party, subject, showAppointment, props.leadingButtons, props.trailingButtons, translate],
    )

    return (
        <>
            <OverflowToolbarPanel
                containerClassName={styles.toolbar}
                toolbarProps={{ size: 'small' }}
                getToolbarButtonProps={button => ({ appearance: 'subtle', size: 'small', disabled: button?.disabled })}
                buttons={buttons}
                filter={list => list?.filter(b => (b.filter ? b.filter(b) : true))}
                overflowButtonLabelKey={'base.item.label'}
            />

            <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                open={newDocumentDialogOpen}
                setOpen={setNewDocumentDialogOpen}
                dialogFormProps={() => ({
                    initialValues: {
                        fileId: file.id,
                        pivotId: party.pivotId,
                    },
                    onSubmit: () => {},
                    validationSchema: getOfficeDocumentDialogBodySchema(translate),
                })}
                dialogFormBody={OfficeDocumentDialogBody}
                dialogFormBodyProps={() => ({
                    context: DocumentContext.Party,
                    file: file,
                    documentRootId: file.documentRootId,
                    party: party,
                    // Feed the combobox the cached folder tree (undefined the first time — it then
                    // loads once and persists via onFoldersLoaded); later opens for any party in this
                    // file reuse the cache instead of re-crawling.
                    folders: folders,
                    onFoldersLoaded: persistFolders,
                })}
            />
        </>
    )
}
